import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, Copy, Plus, Trash2, Film, Lock, Globe, Upload, Link2, RefreshCw, Eye, UserPlus } from "lucide-react";
import { endpoints } from "@/lib/api";
import { uploadToCloudflare } from "@/lib/cfUpload";
import { Card, Btn, handleErr, fmtDateTime } from "./AdminUI";

const inp = "h-10 px-3 rounded-lg bg-[#0A0C10] border border-[#273042] text-white text-sm";
const copy = (t, what) => {
  const p = navigator.clipboard?.writeText ? navigator.clipboard.writeText(t || "") : Promise.reject(new Error("no clipboard"));
  p.then(() => toast.success(`${what} copied`)).catch(() => toast.error(`Could not copy ${what} — select it and copy manually`));
};

const CopyRow = ({ label, value, secret, testid }) => {
  const [show, setShow] = useState(!secret);
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={testid}>
      <span className="w-24 shrink-0 text-slate-400">{label}</span>
      <code className="flex-1 min-w-0 truncate px-2 py-1.5 rounded bg-black border border-[#273042] text-slate-200">{show ? value : "••••••••••••••••"}</code>
      {secret && <button type="button" onClick={() => setShow(!show)} className="text-slate-400 hover:text-white">{show ? "Hide" : "Show"}</button>}
      <button type="button" onClick={() => copy(value, label)} className="text-slate-300 hover:text-white" data-testid={`${testid}-copy`}><Copy className="w-4 h-4" /></button>
    </div>
  );
};

const Recordings = ({ uid }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { endpoints.cfRecordings(uid).then((r) => setRows(r.data.recordings)).catch(handleErr); }, [uid]);
  if (!rows) return <div className="text-xs text-slate-500">Loading recordings…</div>;
  if (!rows.length) return <div className="text-xs text-slate-500" data-testid={`cf-rec-empty-${uid}`}>No recordings yet — every broadcast is saved automatically.</div>;
  return <div className="space-y-1.5">{rows.map((v) => (
    <div key={v.uid} className="flex flex-wrap items-center gap-2 text-xs text-slate-300" data-testid={`cf-rec-${v.uid}`}>
      <Film className="w-3.5 h-3.5" />{fmtDateTime(v.created)} · {v.live ? "recording now" : v.ready ? `${Math.round(v.duration || 0)}s ready` : v.state}
      {v.playback?.iframe && <><a href={v.playback.iframe} target="_blank" rel="noreferrer" className="underline">Watch</a>
        <button type="button" onClick={() => copy(v.embedCode, "Embed code")} className="underline">Copy embed</button></>}
    </div>))}</div>;
};

const InputCard = ({ x, reload }) => {
  const [recs, setRecs] = useState(false);
  const patch = (b) => endpoints.cfLiveUpdate(x.uid, b).then(reload).catch(handleErr);
  const del = () => window.confirm(`Delete live input "${x.name}"? Its stream key stops working.`) && endpoints.cfLiveDelete(x.uid).then(reload).catch(handleErr);
  return (
    <div className="rounded-xl border border-[#273042] p-4 space-y-2" data-testid={`cf-input-${x.uid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-extrabold ${x.connected ? "bg-[#E11D2A] text-white" : "bg-white/10 text-slate-300"}`} data-testid={`cf-state-${x.uid}`}>
          {x.connected && <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" />}{x.connected ? "LIVE" : "OFFLINE"}</span>
        <b className="text-white">{x.name}</b>
        {x.connected && <span className="inline-flex items-center gap-1 text-xs text-white font-bold" data-testid={`cf-viewers-${x.uid}`}><Eye className="w-3.5 h-3.5" />{x.viewers ?? 0} watching</span>}
        {x.owner && <span className="text-[11px] text-slate-400">presenter: {x.owner}</span>}
        {x.recording === "automatic" ? <span className="text-[11px] text-emerald-300">recording: automatic</span>
          : <button type="button" onClick={() => patch({ recording: true })} className="text-[11px] text-amber-300 underline" data-testid={`cf-rec-on-${x.uid}`}>recording off — turn on</button>}
        <div className="ml-auto flex items-center gap-3 text-xs">
          <label className="inline-flex items-center gap-1.5 text-slate-300 cursor-pointer"><input type="checkbox" checked={x.showOnLiveTv} onChange={(e) => patch({ showOnLiveTv: e.target.checked })} data-testid={`cf-livetv-${x.uid}`} />Go live on Live TV</label>
          <button type="button" onClick={() => patch({ private: !x.private })} data-testid={`cf-private-${x.uid}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-white">
            {x.private ? <><Lock className="w-3.5 h-3.5" /> Private</> : <><Globe className="w-3.5 h-3.5" /> Public</>}</button>
          <button type="button" onClick={del} className="text-red-300 hover:text-red-200" data-testid={`cf-delete-${x.uid}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <CopyRow label="RTMPS URL" value={x.rtmps?.url} testid={`cf-rtmps-${x.uid}`} />
      <CopyRow label="Stream key" value={x.rtmps?.streamKey} secret testid={`cf-key-${x.uid}`} />
      <CopyRow label="Player URL" value={x.playback?.iframe} testid={`cf-player-${x.uid}`} />
      <CopyRow label="HLS URL" value={x.playback?.hls} testid={`cf-hls-${x.uid}`} />
      <div className="flex flex-wrap gap-2 pt-1">
        <Btn variant="ghost" onClick={() => copy(x.embedCode, "Embed code")} data-testid={`cf-embed-${x.uid}`}><Copy className="w-3.5 h-3.5" /> Copy embed code</Btn>
        <Btn variant="ghost" onClick={() => setRecs(!recs)} data-testid={`cf-recs-${x.uid}`}><Film className="w-3.5 h-3.5" /> Recordings</Btn>
      </div>
      {x.note && <p className="text-[11px] text-amber-300">{x.note}</p>}
      {recs && <Recordings uid={x.uid} />}
    </div>
  );
};

const Videos = () => {
  const [rows, setRows] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [pct, setPct] = useState(null);
  const load = () => endpoints.cfVideos().then((r) => setRows(r.data.videos)).catch(handleErr);
  useEffect(() => { load(); }, []);
  const fromLink = async () => {
    setBusy("link");
    try { const { data } = await endpoints.cfCopy({ url }); toast.success(data.message || "Cloudflare is fetching the video"); setUrl(""); load(); } catch (e) { handleErr(e); } finally { setBusy(""); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy("file");
    try {
      const { data } = await endpoints.cfUploadUrl({ name: file.name, size: file.size });
      setPct(0);
      await uploadToCloudflare(data.uploadURL, file, setPct, data.tus);
      toast.success("Uploaded — Cloudflare is processing it"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Upload failed"); } finally { setBusy(""); setPct(null); }
  };
  return (
    <Card className="p-5" data-testid="cf-videos">
      <div className="flex items-center gap-2 text-white font-bold mb-3"><Film className="w-4 h-4 text-[#E11D2A]" /> Cloudflare videos (on demand)
        <button type="button" onClick={load} className="ml-auto text-slate-400 hover:text-white"><RefreshCw className="w-4 h-4" /></button></div>
      <div className="flex flex-wrap gap-2 mb-4">
        <label className={`h-10 px-4 rounded-lg bg-[#E11D2A] text-white text-sm font-semibold inline-flex items-center gap-2 cursor-pointer ${busy ? "opacity-60" : ""}`} data-testid="cf-upload-file">
          <Upload className="w-4 h-4" />{busy === "file" ? `Uploading… ${pct ?? 0}%` : "Upload video file"}
          <input type="file" accept="video/*" className="hidden" disabled={!!busy} onChange={(e) => upload(e.target.files?.[0])} /></label>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste a public https:// video link" className={`${inp} flex-1 min-w-[220px]`} data-testid="cf-copy-url" />
        <Btn onClick={fromLink} disabled={!url || !!busy} data-testid="cf-copy-btn"><Link2 className="w-4 h-4" /> Upload from link</Btn>
      </div>
      {!rows ? <div className="text-sm text-slate-400">Loading…</div> : !rows.length ? <div className="text-sm text-slate-500">No videos yet.</div> : (
        <div className="space-y-2">{rows.map((v) => (
          <div key={v.uid} className="flex flex-wrap items-center gap-2 text-sm border-t border-[#273042] pt-2" data-testid={`cf-video-${v.uid}`}>
            <span className="text-white font-semibold truncate max-w-xs">{v.name}</span>
            <span className="text-xs text-slate-500">{fmtDateTime(v.created)} · {v.ready ? `${Math.round(v.duration || 0)}s` : v.state}{v.liveInput ? " · live recording" : ""}{v.private ? " · private" : ""}</span>
            <div className="ml-auto flex gap-3 text-xs">
              {v.playback?.iframe && <a href={v.playback.iframe} target="_blank" rel="noreferrer" className="underline text-slate-300">Player URL</a>}
              <button type="button" onClick={() => copy(v.playback?.iframe, "Player URL")} className="underline text-slate-300">Copy link</button>
              <button type="button" onClick={() => copy(v.embedCode, "Embed code")} className="underline text-slate-300" data-testid={`cf-video-embed-${v.uid}`}>Copy embed</button>
            </div>
          </div>))}</div>)}
    </Card>
  );
};

const Broadcasters = () => {
  const [rows, setRows] = useState([]);
  const [phone, setPhone] = useState("");
  const load = () => endpoints.cfBroadcasters().then((r) => setRows(r.data.broadcasters)).catch(handleErr);
  useEffect(() => { load(); }, []);
  const set = async (ph, on) => { try { const r = await endpoints.cfSetBroadcaster(ph, on); toast.success(on ? `${r.data.name} can now go live` : "Removed"); setPhone(""); load(); } catch (e) { handleErr(e); } };
  return (
    <Card className="p-5" data-testid="cf-broadcasters">
      <div className="flex items-center gap-2 text-white font-bold mb-1"><UserPlus className="w-4 h-4 text-[#E11D2A]" /> Broadcasters (presenters)</div>
      <p className="text-xs text-slate-400 mb-3">Presenters open <b>/broadcast</b> after signing in and get their own stream key — no admin access. Add a registered user by phone, or give a staff login the "Broadcast" permission in Staff & Access.</p>
      <div className="flex gap-2 mb-3"><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Presenter phone, e.g. 0788123456" className={`${inp} flex-1`} data-testid="cf-bc-phone" />
        <Btn onClick={() => set(phone, true)} disabled={!phone} data-testid="cf-bc-add">Add broadcaster</Btn></div>
      {rows.map((b) => (
        <div key={b.id} className="flex items-center gap-2 text-sm border-t border-[#273042] py-2" data-testid={`cf-bc-${b.id}`}>
          <span className="text-white">{b.name || b.phone}</span><span className="text-xs text-slate-500">{b.phone} · {b.kind}</span>
          {b.flag && <button type="button" onClick={() => set(b.phone, false)} className="ml-auto text-xs text-red-300 underline">Remove</button>}
        </div>))}
    </Card>
  );
};

export const CloudflareStreamPanel = () => {
  const [d, setD] = useState(null);
  const [name, setName] = useState("");
  const [priv, setPriv] = useState(false);
  const [tv, setTv] = useState(true);
  const load = () => endpoints.cfLive().then((r) => setD(r.data)).catch(handleErr);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const create = async () => {
    try { await endpoints.cfLiveCreate({ name, private: priv, showOnLiveTv: tv }); toast.success("Live input created"); setName(""); load(); } catch (e) { handleErr(e); }
  };
  return (
    <div className="space-y-6" data-testid="cf-stream-panel">
      <Card className="p-5">
        <div className="flex items-center gap-2 text-white font-bold mb-1"><Radio className="w-4 h-4 text-[#E11D2A]" /> Cloudflare live inputs</div>
        <p className="text-xs text-slate-400 mb-4">Create an input, paste the RTMPS URL + stream key into OBS (or any encoder). The stream starts when you connect and every broadcast is recorded automatically.</p>
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, e.g. Saturday Match Live" className={`${inp} flex-1 min-w-[200px]`} data-testid="cf-new-name" />
          <label className="text-xs text-slate-300 inline-flex items-center gap-1.5"><input type="checkbox" checked={tv} onChange={(e) => setTv(e.target.checked)} data-testid="cf-new-livetv" />Go live on Live TV</label>
          <label className="text-xs text-slate-300 inline-flex items-center gap-1.5"><input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} data-testid="cf-new-private" />Private (signed)</label>
          <Btn onClick={create} data-testid="cf-new-create"><Plus className="w-4 h-4" /> Create live input</Btn>
        </div>
        {!d ? <div className="text-sm text-slate-400">Loading…</div> : <div className="space-y-3">{d.inputs.map((x) => <InputCard key={x.uid} x={x} reload={load} />)}</div>}
      </Card>
      <Broadcasters />
      <Videos />
    </div>
  );
};
